import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { ActeVenteComponent } from './acte-vente/acte-vente.component';
import { AppComponent } from './app.component';
import { AppConfigService } from './app-config.service';
import { AppInitialisationService } from './app-initialisation.service';
import { AppRoutingModule } from './app-routing.module';
import { AppStateModule } from './app-state.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { ConfigurationService } from '@oel/js-configuration';
import { ContentCardService } from './dashboard/services/content-card.service';
import { CoreModule } from './core/core.module';
import { CustomErrorHandler } from './core/services/_index';
import { DonneReferenceService } from './shared/services/donne-reference.service';
import { DsdLibraryModule } from '@dsd/library-angular/dist';
import { EmmenagementComponent } from './emmenagement/emmenagement.component';
import { FormsModule } from '@angular/forms';
import { LibFeatureDecisionModule } from './lib-feature-decision/lib-feature-decision.module';
import { MaterialModule } from './material.module';
import { NotificationModule } from './notification/notification.module';
import { OffreAchatComponent } from './offre-achat/offre-achat.component';
import { ProprieteModule } from './propriete/propriete.module';
import { SharedModule } from './shared/shared.module';
import { TestAutomatiseHelperService } from './lib-cypress-helper/test-automatise-helper.service';
import { forkJoin } from 'rxjs';
import localeFr from '@angular/common/locales/fr';
import { map } from 'rxjs/operators';
// Locales
import { registerLocaleData } from '@angular/common';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { NgxCurrencyDirective } from 'ngx-currency';

registerLocaleData(localeFr, 'fr');

export function appInitialisationFactory(appInitialisationService: AppInitialisationService): Function {
  return () => appInitialisationService.initialiser();
}

export class MultiTranslateHttpLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    public resources: { prefix: string; suffix: string }[] = [
      {
        prefix: '/assets/i18n/',
        suffix: '.json'
      }
    ]
  ) {}

  private readonly CLE_CONFIG_STATIC_SPA_URL = 'oel-static-spa-url';

  public getTranslation(lang: string): any {
    const urlBaseSpaStatique = this.getCheminAbsoluStatique();
    return forkJoin(
      this.resources.map(config => {
        return this.http.get(`${urlBaseSpaStatique}${config.prefix}${lang}${config.suffix}`);
      })
    ).pipe(
      map(response => {
        return response.reduce((a, b) => {
          return Object.assign(a, b);
        });
      })
    );
  }

  private getCheminAbsoluStatique() {
    return ConfigurationService.getConfig(this.CLE_CONFIG_STATIC_SPA_URL);
  }
}

export function translateLoader(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: '/assets/i18n/', suffix: '.json' },
    { prefix: '/assets/i18n/dashboard-', suffix: '.json' },
    { prefix: '/assets/i18n/capacite-', suffix: '.json' },
    { prefix: '/assets/i18n/propriete-', suffix: '.json' },
    { prefix: '/assets/i18n/preautorisation-', suffix: '.json' },
    { prefix: '/assets/i18n/preautorisation-en-ligne-', suffix: '.json' },
    { prefix: '/assets/i18n/offre-achat-', suffix: '.json' },
    { prefix: '/assets/i18n/octroi-', suffix: '.json' },
    { prefix: '/assets/i18n/assurance-pret-', suffix: '.json' },
    { prefix: '/assets/i18n/assurance-habitation-', suffix: '.json' },
    { prefix: '/assets/i18n/acte-vente-', suffix: '.json' },
    { prefix: '/assets/i18n/demenagement-', suffix: '.json' },
    { prefix: '/assets/i18n/navigation-', suffix: '.json' },
    { prefix: '/assets/i18n/infobulles-', suffix: '.json' },
    { prefix: '/assets/i18n/cartes-prochaine-etape-', suffix: '.json' },
    { prefix: '/assets/i18n/monpret-', suffix: '.json' },
    { prefix: '/assets/i18n/caisse-non-eligible-', suffix: '.json' },
    { prefix: '/assets/i18n/page-non-authentifie-', suffix: '.json' },
    { prefix: '/assets/i18n/faq-', suffix: '.json' }
  ]);
}

@NgModule({
  declarations: [AppComponent, OffreAchatComponent, ActeVenteComponent, EmmenagementComponent],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    AppRoutingModule,
    AppStateModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateLoader,
        deps: [HttpClient]
      },
      isolate: true
    }),
    CoreModule.forRoot(),
    SharedModule,
    FormsModule,
    ProprieteModule,
    MaterialModule,
    NotificationModule,
    DsdLibraryModule.forRoot(),
    LibFeatureDecisionModule,
    NgxMaskDirective,
    NgxMaskPipe,
    NgxCurrencyDirective
  ],
  providers: [
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler
    },
    AppConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitialisationFactory,
      deps: [AppInitialisationService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [TestAutomatiseHelperService],
      multi: true
    },
    ContentCardService,
    DonneReferenceService,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}
